import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatImageUrl } from '../utils/formatImageUrl';

const getInitialProfile = () => ({
    name: localStorage.getItem('userName') || 'User',
    email: localStorage.getItem('userEmail') || 'user@example.com',
    phone: localStorage.getItem('userPhone') || '',
});

const navItems = [
    { key: 'profile', label: 'Profile Info', icon: 'ph-user' },
    { key: 'orders', label: 'My Orders', icon: 'ph-shopping-bag-open' },
    // { key: 'returns', label: 'Returns & Refunds', icon: 'ph-arrow-u-up-left' },
    { key: 'reviews', label: 'My Reviews & Ratings', icon: 'ph-star' },
    { key: 'addresses', label: 'Saved Addresses', icon: 'ph-map-pin' },
    { key: 'security', label: 'Security', icon: 'ph-lock-key' },
];

const getReviewStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'approved':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Approved', icon: 'ph-check-circle' };
        case 'rejected':
            return { bg: '#FEE2E2', color: '#B91C1C', label: 'Rejected', icon: 'ph-x-circle' };
        case 'pending':
        default:
            return { bg: '#FEF9C3', color: '#854D0E', label: 'Pending Approval', icon: 'ph-clock' };
    }
};

const emptyAddressForm = {
    fullName: '',
    phone: '',
    street: '',
    apartment: '',
    city: '',
    state: '',
    pincode: '',
    addressType: 'Home',
    isDefault: false
};

const getStatusBadgeStyle = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'delivered':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Delivered' };
        case 'shipped':
            return { bg: '#FEF3C7', color: '#B45309', label: 'Shipped' };
        case 'processing':
            return { bg: '#E0E7FF', color: '#4338CA', label: 'Out for Delivery' };
        case 'confirmed':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Confirmed' };
        case 'cancelled':
            return { bg: '#FEE2E2', color: '#B91C1C', label: 'Cancelled' };
        case 'returned':
            return { bg: '#F3F4F6', color: '#4B5563', label: 'Returned' };
        default:
            return { bg: '#FEF9C3', color: '#854D0E', label: 'Pending' };
    }
};

const getReturnStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'refund_completed':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Refund Completed' };
        case 'replacement_delivered':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Replacement Delivered' };
        case 'approved':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Return Approved' };
        case 'pickup_scheduled':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Pickup Scheduled' };
        case 'picked_up':
            return { bg: '#E0E7FF', color: '#4338CA', label: 'Item Picked Up' };
        case 'inspection_completed':
            return { bg: '#FEF3C7', color: '#B45309', label: 'QC Inspection Passed' };
        case 'refund_initiated':
            return { bg: '#FEF3C7', color: '#B45309', label: 'Refund Initiated' };
        case 'replacement_shipped':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Replacement Dispatched' };
        case 'rejected':
            return { bg: '#FEE2E2', color: '#B91C1C', label: 'Return Rejected' };
        default:
            return { bg: '#FEF9C3', color: '#854D0E', label: 'Under Review' };
    }
};

const getReturnStageNumber = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'pending_review':
            return 1;
        case 'approved':
            return 2;
        case 'pickup_scheduled':
        case 'picked_up':
            return 3;
        case 'inspection_completed':
        case 'refund_initiated':
        case 'replacement_shipped':
            return 4;
        case 'refund_completed':
        case 'replacement_delivered':
            return 5;
        case 'rejected':
            return -1;
        default:
            return 1;
    }
};

const RETURN_STAGES = [
    { id: 1, label: 'Submitted', desc: 'Request received' },
    { id: 2, label: 'Approved', desc: 'Review passed' },
    { id: 3, label: 'Pickup', desc: 'Reverse transit' },
    { id: 4, label: 'Inspection', desc: 'QC check' },
    { id: 5, label: 'Completed', desc: 'Refund Settled' },
];

const Account = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('profile');

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tab = queryParams.get('tab');
        if (tab && ['profile', 'orders', 'reviews', 'addresses', 'security'].includes(tab)) {
            setActiveTab(tab);
        } else if (tab === 'returns') {
            setActiveTab('orders');
        }
    }, [location.search]);

    // Profile state
    const [profile, setProfile] = useState(getInitialProfile);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileDraft, setProfileDraft] = useState(getInitialProfile);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    // Orders state
    const [orders, setOrders] = useState([]);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [selectedOrderForReturn, setSelectedOrderForReturn] = useState(null);
    const [returnReason, setReturnReason] = useState('Damaged Product');
    const [returnComments, setReturnComments] = useState('');
    const [returnType, setReturnType] = useState('refund');
    const [returnVideoFile, setReturnVideoFile] = useState(null);
    const [returnVideoPreview, setReturnVideoPreview] = useState(null);
    const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);

    // Returns tab state
    const [returnsList, setReturnsList] = useState([]);
    const [isLoadingReturns, setIsLoadingReturns] = useState(false);
    const [previewVideoModal, setPreviewVideoModal] = useState(null);

    // Reviews tab state
    const [purchasedReviewData, setPurchasedReviewData] = useState({ items: [], counts: {} });
    const [myReviews, setMyReviews] = useState([]);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);
    const [reviewsSubTab, setReviewsSubTab] = useState('to_review'); // 'to_review' | 'my_reviews' | 'all'
    const [reviewModalData, setReviewModalData] = useState(null);
    const [hoverRating, setHoverRating] = useState(0);
    const [isSubmittingReviewForm, setIsSubmittingReviewForm] = useState(false);
    const [showDeleteReviewModal, setShowDeleteReviewModal] = useState(null);
    const [isDeletingReview, setIsDeletingReview] = useState(false);

    // Address state
    const [addresses, setAddresses] = useState([]);
    const [addressForm, setAddressForm] = useState(null);
    const [editingAddressId, setEditingAddressId] = useState(null);

    // Security (Password) state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // Fetch user profile from backend
    const fetchProfile = useCallback(async () => {
        try {
            const res = await api.get('/api/auth/me');
            if (res.data) {
                setProfile(res.data);
                setProfileDraft(res.data);
                localStorage.setItem('userName', res.data.name || '');
                localStorage.setItem('userEmail', res.data.email || '');
                localStorage.setItem('userPhone', res.data.phone || '');
            }
        } catch (err) {
            console.error('Failed to fetch profile', err);
        }
    }, []);

    // Fetch Orders
    const fetchOrders = useCallback(async () => {
        setIsLoadingOrders(true);
        try {
            const res = await api.get('/api/orders');
            setOrders(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch orders', err);
        } finally {
            setIsLoadingOrders(false);
        }
    }, []);

    // Fetch Returns
    const fetchReturns = useCallback(async () => {
        setIsLoadingReturns(true);
        try {
            const res = await api.get('/api/returns');
            setReturnsList(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch returns', err);
        } finally {
            setIsLoadingReturns(false);
        }
    }, []);

    // Fetch Reviews & Purchased Items
    const fetchReviewData = useCallback(async () => {
        setIsLoadingReviews(true);
        try {
            const [purchasedRes, myReviewsRes] = await Promise.all([
                api.get('/api/reviews/purchased-items'),
                api.get('/api/reviews/my-reviews')
            ]);
            setPurchasedReviewData(purchasedRes.data || { items: [], counts: {} });
            setMyReviews(Array.isArray(myReviewsRes.data) ? myReviewsRes.data : []);
        } catch (err) {
            console.error('Failed to fetch review data', err);
            toast.error('Failed to load reviews');
        } finally {
            setIsLoadingReviews(false);
        }
    }, []);

    // Fetch Addresses
    const fetchAddresses = useCallback(async () => {
        try {
            const res = await api.get('/api/address');
            setAddresses(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch addresses', err);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    useEffect(() => {
        if (activeTab === 'orders') fetchOrders();
        if (activeTab === 'returns') fetchReturns();
        if (activeTab === 'reviews') fetchReviewData();
        if (activeTab === 'addresses') fetchAddresses();
    }, [activeTab, fetchOrders, fetchReturns, fetchReviewData, fetchAddresses]);

    // Profile handlers
    const saveProfile = async (e) => {
        e.preventDefault();
        try {
            const res = await api.put('/api/auth/me', {
                name: profileDraft.name,
                phone: profileDraft.phone
            });
            setProfile(res.data);
            localStorage.setItem('userName', res.data.name || '');
            localStorage.setItem('userPhone', res.data.phone || '');
            setIsEditingProfile(false);
            toast.success('Profile updated successfully!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update profile');
        }
    };

    // 2-Hour Order Cancellation Helpers & Handlers
    const isOrderCancellable = (order) => {
        if (!order || !order.createdAt) return false;
        const status = (order.status || '').toLowerCase();
        if (!['pending', 'confirmed'].includes(status)) return false;
        const orderTime = new Date(order.createdAt).getTime();
        if (isNaN(orderTime)) return false;
        const hours = (Date.now() - orderTime) / (1000 * 60 * 60);
        return hours >= 0 && hours <= 2;
    };

    const getCancellationTimeRemaining = (order) => {
        if (!order || !order.createdAt) return null;
        const orderTime = new Date(order.createdAt).getTime();
        if (isNaN(orderTime)) return null;
        const elapsedMs = Date.now() - orderTime;
        const remainingMs = (2 * 60 * 60 * 1000) - elapsedMs;
        if (remainingMs <= 0) return null;
        const hours = Math.floor(remainingMs / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
            return `${hours}h ${minutes}m left`;
        }
        return `${minutes}m left`;
    };

    const executeCancelOrder = async (orderId) => {
        const loadingToast = toast.loading("Cancelling order...");
        try {
            await api.patch(`/api/returns/cancel-order/${orderId}`);
            toast.dismiss(loadingToast);
            toast.success("Order cancelled successfully!");
            fetchOrders();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || "Failed to cancel order");
        }
    };

    const handleCancelOrder = (order) => {
        const orderId = typeof order === 'object' ? order.id : order;
        const orderRef = (typeof order === 'object' && (order.referenceSlug || order.id)) || orderId;

        toast((t) => (
            <div style={{ minWidth: '250px' }}>
                <div className="d-flex align-items-center gap-8 mb-6">
                    <i className="ph-bold ph-warning-circle text-danger-600" style={{ fontSize: '20px' }} />
                    <span className="fw-bold text-neutral-900" style={{ fontSize: '14px' }}>Cancel Order #{orderRef}?</span>
                </div>
                <p className="text-xs text-gray-600 mb-12">
                    Are you sure you want to cancel this order?
                </p>
                <div className="d-flex align-items-center justify-content-end gap-8">
                    <button
                        type="button"
                        className="py-6 px-12 rounded-6 text-xs fw-semibold"
                        style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#334155', cursor: 'pointer' }}
                        onClick={() => toast.dismiss(t.id)}
                    >
                        Keep Order
                    </button>
                    <button
                        type="button"
                        className="py-6 px-14 rounded-6 text-xs fw-bold"
                        style={{ background: '#DC2626', border: 'none', color: '#FFFFFF', cursor: 'pointer' }}
                        onClick={() => {
                            toast.dismiss(t.id);
                            executeCancelOrder(orderId);
                        }}
                    >
                        Yes, Cancel
                    </button>
                </div>
            </div>
        ), {
            duration: 8000,
            style: {
                borderRadius: '12px',
                background: '#FFFFFF',
                color: '#1E293B',
                boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.2), 0 0 0 1px rgba(15, 23, 42, 0.05)',
                padding: '14px 16px',
            },
        });
    };

    // Video file handlers
    const handleVideoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) {
            toast.error("Video file is too large. Maximum allowed size is 50MB.");
            return;
        }

        if (!file.type.startsWith('video/')) {
            toast.error("Please upload a valid video file (MP4 or MOV).");
            return;
        }

        setReturnVideoFile(file);
        if (returnVideoPreview) {
            URL.revokeObjectURL(returnVideoPreview);
        }
        setReturnVideoPreview(URL.createObjectURL(file));
    };

    const handleRemoveVideo = () => {
        if (returnVideoPreview) {
            URL.revokeObjectURL(returnVideoPreview);
        }
        setReturnVideoFile(null);
        setReturnVideoPreview(null);
    };

    // Submit Return Request
    const handleRequestReturn = async (e) => {
        e.preventDefault();
        if (!selectedOrderForReturn) return;

        if (!returnVideoFile) {
            toast.error("An unboxing video is mandatory for return requests.");
            return;
        }

        setIsSubmittingReturn(true);
        try {
            const firstItem = selectedOrderForReturn.items?.[0];
            const formData = new FormData();
            formData.append('orderId', selectedOrderForReturn.id);
            formData.append('orderItemId', firstItem?.id || '');
            formData.append('returnType', returnType);
            formData.append('reason', returnReason);
            formData.append('comments', returnComments);
            formData.append('return_quantity', firstItem?.quantity || 1);
            formData.append('video', returnVideoFile);

            await api.post('/api/returns', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success("Return request submitted successfully! Our team will review it.");
            setSelectedOrderForReturn(null);
            setReturnComments('');
            handleRemoveVideo();
            fetchOrders();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to submit return request");
        } finally {
            setIsSubmittingReturn(false);
        }
    };

    // Address handlers
    const openAddAddress = () => {
        setEditingAddressId(null);
        setAddressForm(emptyAddressForm);
    };

    const openEditAddress = (addr) => {
        setEditingAddressId(addr.id);
        setAddressForm({
            fullName: addr.fullName || '',
            phone: addr.phone || '',
            street: addr.street || '',
            apartment: addr.apartment || '',
            city: addr.city || '',
            state: addr.state || '',
            pincode: addr.pincode || '',
            addressType: addr.addressType || 'Home',
            isDefault: addr.isDefault || false
        });
    };

    const saveAddress = async (e) => {
        e.preventDefault();
        try {
            if (editingAddressId) {
                await api.put(`/api/address/${editingAddressId}`, addressForm);
                toast.success("Address updated!");
            } else {
                await api.post('/api/address', addressForm);
                toast.success("Address added!");
            }
            setAddressForm(null);
            setEditingAddressId(null);
            fetchAddresses();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save address");
        }
    };

    const deleteAddress = async (id) => {
        if (!window.confirm("Are you sure you want to delete this address?")) return;
        try {
            await api.delete(`/api/address/${id}`);
            toast.success("Address deleted!");
            fetchAddresses();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete address");
        }
    };

    const setDefaultAddress = async (id) => {
        try {
            await api.patch(`/api/address/${id}/default`);
            toast.success("Default address updated!");
            fetchAddresses();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to set default address");
        }
    };

    // Password Update
    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        if (newPassword !== confirmNewPassword) {
            toast.error("New passwords do not match");
            return;
        }

        setIsUpdatingPassword(true);
        try {
            await api.put('/api/auth/update-password', {
                currentPassword,
                newPassword
            });
            toast.success("Password updated successfully!");
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update password");
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    // Review Modal & CRUD Handlers
    const openCreateReviewModal = (item) => {
        setReviewModalData({
            mode: 'create',
            productId: item.productId,
            childComboId: item.childComboId,
            name: item.name,
            image: item.image,
            selectedVariantName: item.selectedVariantName,
            rating: 5,
            feedback: '',
        });
        setHoverRating(0);
    };

    const openEditReviewModal = (review, itemDetails = null) => {
        const prod = review.product || review.childCombo || itemDetails;
        let img = review.product?.image || review.childCombo?.image;
        if (Array.isArray(img) && img.length > 0) img = img[0];
        else if (typeof img === 'string') {
            try {
                const parsed = JSON.parse(img);
                if (Array.isArray(parsed)) img = parsed[0];
            } catch (e) {}
        }
        if (!img && itemDetails?.image) img = itemDetails.image;

        setReviewModalData({
            mode: 'edit',
            reviewId: review.id,
            productId: review.productId,
            childComboId: review.childComboId,
            name: prod?.name || itemDetails?.name || 'Purchased Item',
            image: img,
            selectedVariantName: itemDetails?.selectedVariantName || null,
            rating: Number(review.rating) || 5,
            feedback: review.feedback || '',
        });
        setHoverRating(0);
    };

    const handleSaveReview = async (e) => {
        e.preventDefault();
        if (!reviewModalData) return;

        if (!reviewModalData.rating || reviewModalData.rating < 1 || reviewModalData.rating > 5) {
            toast.error('Please select a star rating between 1 and 5');
            return;
        }

        if (!reviewModalData.feedback?.trim()) {
            toast.error('Please enter your review feedback');
            return;
        }

        setIsSubmittingReviewForm(true);
        try {
            if (reviewModalData.mode === 'create') {
                await api.post('/api/reviews', {
                    productId: reviewModalData.productId || undefined,
                    childComboId: reviewModalData.childComboId || undefined,
                    rating: reviewModalData.rating,
                    feedback: reviewModalData.feedback.trim(),
                });
                toast.success('Review submitted! It will appear once approved by admin.');
            } else {
                await api.put(`/api/reviews/my-reviews/${reviewModalData.reviewId}`, {
                    rating: reviewModalData.rating,
                    feedback: reviewModalData.feedback.trim(),
                });
                toast.success('Review updated successfully!');
            }
            setReviewModalData(null);
            fetchReviewData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit review');
        } finally {
            setIsSubmittingReviewForm(false);
        }
    };

    const handleDeleteReview = async () => {
        if (!showDeleteReviewModal) return;
        const reviewId = showDeleteReviewModal.id;
        setIsDeletingReview(true);
        try {
            await api.delete(`/api/reviews/my-reviews/${reviewId}`);
            toast.success('Review deleted successfully');
            setShowDeleteReviewModal(null);
            fetchReviewData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete review');
        } finally {
            setIsDeletingReview(false);
        }
    };

    // Logout
    const handleConfirmLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPhone');
        window.location.href = '/login';
    };

    return (
        <section className="account py-40 py-sm-60 py-lg-80">
            <style>{`
                .acc-sidebar-card { border: 1px solid #E2E8F0; border-radius: 16px; padding: 24px 18px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.03); }
                @media (min-width: 992px) { .acc-sidebar-card { padding: 32px 24px; } }
                .acc-avatar { width: 72px; height: 72px; border-radius: 50%; background: var(--main-50, #E7F7EC); color: var(--main-600, #2FA84F); font-size: 26px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
                .acc-nav-item { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 10px; font-weight: 500; color: #4B4B4B; background: none; border: none; width: 100%; text-align: left; margin-bottom: 8px; cursor: pointer; transition: all 0.2s; }
                .acc-nav-item.active { background: var(--main-50, #E7F7EC); color: var(--main-600, #2FA84F); font-weight: 600; }
                .acc-logout-btn { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 12px; border-radius: 10px; border: 1.5px solid #FCA5A5; color: #DC2626; background: #FEF2F2; font-weight: 600; margin-top: 16px; cursor: pointer; transition: background 0.2s; }
                .acc-logout-btn:hover { background: #FEE2E2; }
                
                /* Mobile Horizontal Tabs */
                .acc-mobile-tabs-container {
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    margin-bottom: 16px;
                    padding-bottom: 4px;
                }
                .acc-mobile-tabs-container::-webkit-scrollbar { display: none; }
                .acc-mobile-tabs {
                    display: flex;
                    gap: 8px;
                    width: max-content;
                }
                .acc-mobile-tab-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 16px;
                    border-radius: 999px;
                    font-size: 13px;
                    font-weight: 600;
                    background: #fff;
                    border: 1.5px solid #E2E8F0;
                    color: #4B4B4B;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: all 0.2s ease;
                }
                .acc-mobile-tab-btn.active {
                    background: var(--main-50, #E7F7EC);
                    color: var(--main-600, #2FA84F);
                    border-color: var(--main-600, #2FA84F);
                }

                .acc-content-card { border: 1px solid #E2E8F0; border-radius: 16px; padding: 20px 16px; min-height: 360px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.03); }
                @media (min-width: 576px) { .acc-content-card { padding: 32px 28px; } }
                .order-card { border: 1px solid #E2E8F0; border-radius: 16px; padding: 20px; margin-bottom: 20px; background: #fff; transition: all 0.2s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
                .order-card:hover { border-color: #CBD5E1; box-shadow: 0 4px 16px rgba(15,23,42,0.06); }
                .order-status-badge { font-size: 11px; font-weight: 700; text-transform: uppercase; padding: 5px 12px; border-radius: 999px; letter-spacing: 0.03em; }
                .order-item-thumb { width: 64px; height: 64px; border-radius: 10px; object-fit: contain; background: #FFFFFF; border: 1px solid #E2E8F0; flex-shrink: 0; padding: 2px; }
                .return-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(15, 23, 42, 0.65);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    backdrop-filter: blur(6px);
                    padding: 16px;
                }
                .return-modal-box {
                    background: #ffffff;
                    width: 540px;
                    max-width: 100%;
                    border-radius: 20px;
                    padding: 28px 24px;
                    box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
                    border: 1px solid #E2E8F0;
                }
                @media (max-width: 575px) {
                    .return-modal-box {
                        padding: 20px 16px;
                        border-radius: 16px;
                    }
                }

                /* Modern Return & Refund UI */
                .rt-card {
                    border: 1px solid #E2E8F0;
                    border-radius: 16px;
                    background: #FFFFFF;
                    box-shadow: 0 2px 10px -2px rgba(15, 23, 42, 0.04);
                    margin-bottom: 24px;
                    overflow: hidden;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .rt-card:hover {
                    border-color: #CBD5E1;
                    box-shadow: 0 6px 20px -4px rgba(15, 23, 42, 0.08);
                }
                .rt-card-top {
                    padding: 16px 22px;
                    background: #F8FAFC;
                    border-bottom: 1px solid #E2E8F0;
                }
                .rt-pill-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    display: inline-block;
                    margin-right: 6px;
                }
                .rt-stepper-wrap {
                    padding: 24px 22px 20px;
                    background: #FFFFFF;
                    border-bottom: 1px solid #F1F5F9;
                }
                .rt-stepper-line {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }
                .rt-stepper-track-bg {
                    position: absolute;
                    top: 15px;
                    left: 40px;
                    right: 40px;
                    height: 3px;
                    background: #E2E8F0;
                    z-index: 1;
                    border-radius: 3px;
                }
                .rt-stepper-track-fill {
                    position: absolute;
                    top: 15px;
                    left: 40px;
                    height: 3px;
                    background: linear-gradient(90deg, #10B981 0%, #059669 100%);
                    z-index: 1;
                    border-radius: 3px;
                    transition: width 0.4s ease;
                }
                .rt-step-item {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    flex: 1;
                    padding: 0 6px;
                }
                .rt-step-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: #FFFFFF;
                    border: 2px solid #CBD5E1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 700;
                    color: #64748B;
                    margin-bottom: 8px;
                    transition: all 0.25s ease;
                }
                .rt-step-circle.done {
                    background: #10B981;
                    border-color: #10B981;
                    color: #FFFFFF;
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
                }
                .rt-step-circle.active {
                    background: #FFFFFF;
                    border-color: #10B981;
                    color: #059669;
                    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
                    font-weight: 800;
                }
                .rt-node-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #0F172A;
                    margin-bottom: 2px;
                    line-height: 1.3;
                }
                .rt-node-desc {
                    font-size: 11px;
                    color: #64748B;
                    line-height: 1.3;
                }

                .rt-product-box {
                    padding: 16px;
                    border-radius: 12px;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    transition: border-color 0.2s ease;
                }
                .rt-product-thumb {
                    width: 68px;
                    height: 68px;
                    border-radius: 10px;
                    border: 1px solid #E2E8F0;
                    background: #FFFFFF;
                    object-fit: contain;
                    flex-shrink: 0;
                    padding: 4px;
                }
                .rt-info-card {
                    padding: 16px;
                    border-radius: 12px;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                @media (max-width: 640px) {
                    .rt-card-top {
                        padding: 14px 16px;
                    }
                    .rt-stepper-wrap {
                        padding: 16px 14px;
                    }
                    .rt-stepper-line {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                        padding-left: 6px;
                    }
                    .rt-stepper-track-bg, .rt-stepper-track-fill {
                        top: 14px;
                        bottom: 14px;
                        left: 21px;
                        width: 2px;
                        height: auto;
                        right: auto;
                    }
                    .rt-step-item {
                        flex-direction: row;
                        align-items: flex-start;
                        text-align: left;
                        gap: 14px;
                        width: 100%;
                        padding: 0;
                    }
                    .rt-step-circle {
                        margin-bottom: 0;
                        width: 28px;
                        height: 28px;
                        font-size: 11px;
                        flex-shrink: 0;
                    }
                    .rt-node-title {
                        font-size: 13px;
                        font-weight: 700;
                        color: #0F172A;
                        line-height: 1.3;
                    }
                    .rt-node-desc {
                        font-size: 11px;
                        color: #64748B;
                        line-height: 1.3;
                    }
                }

                /* Review System Styling */
                .review-summary-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .review-metric-card {
                    border: 1px solid #E2E8F0;
                    border-radius: 14px;
                    padding: 16px 18px;
                    background: #FFFFFF;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .review-metric-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px -4px rgba(15, 23, 42, 0.06);
                }
                .review-subtab-bar {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    margin-bottom: 24px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #E2E8F0;
                }
                .review-subtab-btn {
                    padding: 8px 18px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    border: 1px solid #E2E8F0;
                    background: #FFFFFF;
                    color: #475569;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .review-subtab-btn:hover {
                    border-color: #CBD5E1;
                    color: #0F172A;
                }
                .review-subtab-btn.active {
                    background: var(--main-600, #2FA84F);
                    color: #FFFFFF;
                    border-color: var(--main-600, #2FA84F);
                    box-shadow: 0 2px 8px rgba(47, 168, 79, 0.25);
                }
                .review-card-item {
                    border: 1px solid #E2E8F0;
                    border-radius: 16px;
                    background: #FFFFFF;
                    padding: 22px;
                    margin-bottom: 20px;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
                }
                .review-card-item:hover {
                    border-color: #CBD5E1;
                    box-shadow: 0 8px 24px -4px rgba(15, 23, 42, 0.08);
                }
                .review-thumb {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    object-fit: contain;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    flex-shrink: 0;
                    padding: 4px;
                }
                .review-quote-box {
                    background: #F8FAFC;
                    border: 1px solid #E2E8F0;
                    border-left: 3.5px solid var(--main-600, #2FA84F);
                    border-radius: 0 10px 10px 0;
                    padding: 14px 18px;
                    font-size: 14px;
                    color: #334155;
                    line-height: 1.6;
                }
                .rev-action-btn {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    padding: 7px 16px !important;
                    border-radius: 8px !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    line-height: 1 !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    text-decoration: none !important;
                }
                .rev-action-btn-edit {
                    border: 1.5px solid #10B981 !important;
                    background-color: #F0FDF4 !important;
                    color: #047857 !important;
                }
                .rev-action-btn-edit:hover {
                    background-color: #DCFCE7 !important;
                    border-color: #059669 !important;
                    color: #065F46 !important;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
                }
                .rev-action-btn-delete {
                    border: 1.5px solid #FCA5A5 !important;
                    background-color: #FEF2F2 !important;
                    color: #DC2626 !important;
                }
                .rev-action-btn-delete:hover {
                    background-color: #FEE2E2 !important;
                    border-color: #EF4444 !important;
                    color: #B91C1C !important;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
                }
                .btn-cancel-order {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    padding: 8px 16px !important;
                    border-radius: 8px !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                    line-height: 1 !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    border: 1.5px solid #EF4444 !important;
                    background-color: #FEF2F2 !important;
                    color: #DC2626 !important;
                    text-decoration: none !important;
                }
                .btn-cancel-order:hover {
                    background-color: #DC2626 !important;
                    border-color: #DC2626 !important;
                    color: #FFFFFF !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.25) !important;
                }
                .btn-cancel-order i {
                    color: inherit !important;
                    font-size: 14px !important;
                }
                .star-selector-box {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .star-clickable {
                    font-size: 32px;
                    line-height: 1;
                    cursor: pointer;
                    background: none;
                    border: none;
                    padding: 0;
                    transition: transform 0.15s ease, color 0.15s ease;
                }
                .star-clickable:hover {
                    transform: scale(1.18);
                }
                .star-label-badge {
                    display: inline-block;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 6px;
                    background: #FEF3C7;
                    color: #B45309;
                }
            `}</style>

            {/* Video Lightbox Modal */}
            {previewVideoModal && (
                <div className="return-modal-overlay" onClick={() => setPreviewVideoModal(null)}>
                    <div className="return-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, background: '#000', padding: 16 }}>
                        <div className="flex-between mb-12 text-white">
                            <span className="text-sm fw-bold">Unboxing Video Proof</span>
                            <button type="button" className="btn p-0 text-white" onClick={() => setPreviewVideoModal(null)}>✕</button>
                        </div>
                        <video controls autoPlay src={previewVideoModal} className="w-100 rounded-8" style={{ maxHeight: '70vh' }} />
                    </div>
                </div>
            )}

            {/* Logout Modal */}
            {showLogoutModal && (
                <div className="return-modal-overlay" onClick={() => setShowLogoutModal(false)}>
                    <div className="return-modal-box text-center" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <h4 className="mb-12">Sign Out</h4>
                        <p className="text-gray-600 mb-24">Are you sure you want to log out of your account?</p>
                        <div className="flex-align gap-16 justify-content-center">
                            <button
                                type="button"
                                className="py-10 px-22 rounded-8 text-sm fw-bold transition"
                                style={{ background: '#F1F5F9', border: '1.5px solid #CBD5E1', color: '#1E293B', cursor: 'pointer' }}
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                            <button className="btn py-10 px-20 rounded-8" style={{ background: '#E0503A', color: '#fff' }} onClick={handleConfirmLogout}>Yes, Logout</button>
                        </div>
                    </div>
                </div>
            )}



            {/* Review Create & Edit Modal */}
            {reviewModalData && (
                <div className="return-modal-overlay" onClick={() => setReviewModalData(null)}>
                    <div className="return-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                        <div className="d-flex align-items-center justify-content-between mb-20 pb-12 border-bottom border-gray-100">
                            <div className="d-flex align-items-center gap-10">
                                {/* <div
                                    className="rounded-8 flex-center text-white flex-shrink-0"
                                    style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                                >
                                    <i className="ph-fill ph-star text-lg" />
                                </div> */}
                                <div>
                                    <h5 className="mb-0 fw-bold text-neutral-900 font-heading">
                                        {reviewModalData.mode === 'edit' ? 'Edit Your Review' : 'Write Product Review'}
                                    </h5>
                                    <span className="text-xs text-gray-500">Share your genuine experience</span>
                                </div>
                            </div>
                            <button type="button" className="btn p-0 text-gray-400 hover-text-neutral-900" onClick={() => setReviewModalData(null)}>
                                <i className="ph ph-x text-lg" />
                            </button>
                        </div>

                        {/* Product Summary Header */}
                        <div className="d-flex align-items-center gap-14 p-12 rounded-12 bg-gray-50 border border-gray-200 mb-20">
                            <img
                                src={formatImageUrl(reviewModalData.image)}
                                alt={reviewModalData.name || 'Product'}
                                className="review-thumb"
                                style={{ width: 54, height: 54 }}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                }}
                            />
                            <div className="min-w-0">
                                <h6 className="text-sm fw-bold text-neutral-900 mb-2 text-line-1">{reviewModalData.name}</h6>
                                {reviewModalData.selectedVariantName && (
                                    <span className="text-xs text-gray-500 d-block mb-2">Variant: {reviewModalData.selectedVariantName}</span>
                                )}
                                <span className="badge rounded-pill bg-success-50 text-success-700 border border-success-200 text-[10px] py-2 px-8 fw-bold">
                                    <i className="ph-fill ph-check-circle me-4" /> Verified Purchase
                                </span>
                            </div>
                        </div>

                        <form onSubmit={handleSaveReview}>
                            {/* Star Rating Selector */}
                            <div className="mb-20">
                                <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                    Overall Rating <span className="text-danger-600">*</span>
                                </label>
                                <div className="d-flex align-items-center gap-14 flex-wrap">
                                    <div className="star-selector-box">
                                        {[1, 2, 3, 4, 5].map((star) => {
                                            const activeStars = hoverRating || reviewModalData.rating || 0;
                                            const isFilled = star <= activeStars;
                                            return (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    className="star-clickable"
                                                    onMouseEnter={() => setHoverRating(star)}
                                                    onMouseLeave={() => setHoverRating(0)}
                                                    onClick={() => setReviewModalData(prev => ({ ...prev, rating: star }))}
                                                    style={{
                                                        color: isFilled ? '#F59E0B' : '#E2E8F0',
                                                    }}
                                                >
                                                    ★
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="star-label-badge">
                                        {(hoverRating || reviewModalData.rating) === 5 ? '5 ★ - Excellent' :
                                         (hoverRating || reviewModalData.rating) === 4 ? '4 ★ - Very Good' :
                                         (hoverRating || reviewModalData.rating) === 3 ? '3 ★ - Good' :
                                         (hoverRating || reviewModalData.rating) === 2 ? '2 ★ - Fair' :
                                         (hoverRating || reviewModalData.rating) === 1 ? '1 ★ - Poor' : 'Select Rating'}
                                    </span>
                                </div>
                            </div>

                            {/* Feedback Textarea */}
                            <div className="mb-24">
                                <div className="d-flex align-items-center justify-content-between mb-8">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-0" style={{ letterSpacing: '0.04em' }}>
                                        Review Feedback <span className="text-danger-600">*</span>
                                    </label>
                                    <span className="text-[11px] text-gray-400">
                                        {(reviewModalData.feedback || '').length} characters
                                    </span>
                                </div>
                                <textarea
                                    className="common-input py-12 px-16 rounded-10 text-sm w-100"
                                    rows={4}
                                    placeholder="Write about product quality, taste, freshness, packaging, or delivery experience..."
                                    value={reviewModalData.feedback || ''}
                                    onChange={(e) => setReviewModalData(prev => ({ ...prev, feedback: e.target.value }))}
                                    required
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex align-items-center justify-content-end gap-12 pt-16 border-top border-gray-100">
                                <button
                                    type="button"
                                    className="py-10 px-22 rounded-8 text-sm fw-bold transition"
                                    style={{
                                        background: '#F1F5F9',
                                        border: '1.5px solid #CBD5E1',
                                        color: '#1E293B',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setReviewModalData(null)}
                                    disabled={isSubmittingReviewForm}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-main py-10 px-24 rounded-8 text-sm fw-bold d-inline-flex align-items-center gap-6 shadow-sm"
                                    disabled={isSubmittingReviewForm}
                                >
                                    {isSubmittingReviewForm ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ph-bold ph-paper-plane-tilt" />
                                            {reviewModalData.mode === 'edit' ? 'Update Review' : 'Submit Review'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Review Confirmation Modal */}
            {showDeleteReviewModal && (
                <div className="return-modal-overlay" onClick={() => setShowDeleteReviewModal(null)}>
                    <div className="return-modal-box text-center" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="flex-center rounded-circle mx-auto mb-16" style={{ width: 56, height: 56, background: '#FEE2E2', color: '#DC2626' }}>
                            <i className="ph-bold ph-trash text-2xl" />
                        </div>
                        <h5 className="mb-8 fw-bold text-neutral-900">Delete Review?</h5>
                        <p className="text-gray-600 text-sm mb-24">
                            Are you sure you want to delete your review for <strong>{showDeleteReviewModal.product?.name || showDeleteReviewModal.childCombo?.name || 'this item'}</strong>? This action cannot be undone.
                        </p>
                        <div className="d-flex align-items-center justify-content-center gap-12">
                            <button
                                type="button"
                                className="py-10 px-22 rounded-8 text-sm fw-bold transition"
                                style={{ background: '#F1F5F9', border: '1.5px solid #CBD5E1', color: '#1E293B', cursor: 'pointer' }}
                                onClick={() => setShowDeleteReviewModal(null)}
                                disabled={isDeletingReview}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn py-10 px-24 rounded-8 text-sm fw-bold"
                                style={{ background: '#DC2626', color: '#FFFFFF' }}
                                onClick={handleDeleteReview}
                                disabled={isDeletingReview}
                            >
                                {isDeletingReview ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Return Request Modal (Disabled) */}
            {false && selectedOrderForReturn && (
                <div className="return-modal-overlay" onClick={() => { setSelectedOrderForReturn(null); handleRemoveVideo(); }}>
                    <div className="return-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="flex-between mb-20">
                            <h5 className="mb-0">Request Return / Refund</h5>
                            <button type="button" className="btn p-0 text-gray-500" onClick={() => { setSelectedOrderForReturn(null); handleRemoveVideo(); }}>✕</button>
                        </div>
                        <p className="text-sm text-gray-600 mb-20">
                            Order <strong>#{selectedOrderForReturn.referenceSlug || selectedOrderForReturn.id}</strong>
                        </p>
                        <form onSubmit={handleRequestReturn}>
                            <div className="mb-16">
                                <label className="text-sm fw-medium mb-6 d-block">Reason for Return</label>
                                <select className="common-input" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                                    <option value="Damaged Product">Damaged or Leaking Item</option>
                                    <option value="Wrong Item Delivered">Wrong Item Delivered</option>
                                    <option value="Quality Not Satisfactory">Quality Not Satisfactory</option>
                                    <option value="Missing Item">Missing Item</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            {/* Mandatory Unboxing Video Field */}
                            <div className="mb-16">
                                <label className="text-sm fw-bold text-neutral-900 mb-6 d-flex align-items-center justify-content-between">
                                    <span>
                                        Unboxing Video <span className="text-danger-600">*</span>
                                    </span>
                                    <span className="text-xs text-gray-500 font-normal">Max 50MB (MP4, MOV)</span>
                                </label>

                                {!returnVideoPreview ? (
                                    <label
                                        className="d-flex flex-column align-items-center justify-content-center p-20 rounded-12 border border-gray-300 bg-gray-50 text-center cursor-pointer transition hover-border-main-600"
                                        style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                    >
                                        <input
                                            type="file"
                                            accept="video/mp4,video/quicktime,video/*"
                                            className="d-none"
                                            onChange={handleVideoChange}
                                            required
                                        />
                                        <div className="flex-center rounded-circle bg-white shadow-xs mb-8" style={{ width: 44, height: 44, color: '#2FA84F' }}>
                                            <i className="ph-fill ph-video-camera text-2xl" />
                                        </div>
                                        <span className="text-xs fw-bold text-neutral-900 mb-2">Click or drag package unboxing video here</span>
                                        <span className="text-[11px] text-gray-500">Unboxing video is mandatory showing the intact outer box & package opening</span>
                                    </label>
                                ) : (
                                    <div className="rounded-12 border border-gray-200 overflow-hidden bg-black p-8">
                                        <video
                                            controls
                                            src={returnVideoPreview}
                                            className="w-100 rounded-8"
                                            style={{ maxHeight: '200px', objectFit: 'contain', background: '#000' }}
                                        />
                                        <div className="d-flex align-items-center justify-content-between p-8 bg-white rounded-8 mt-6">
                                            <div className="d-flex align-items-center gap-8 min-w-0">
                                                <i className="ph-bold ph-video text-main-600 text-lg flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <span className="text-xs fw-bold text-neutral-900 text-truncate d-block">
                                                        {returnVideoFile?.name}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500">
                                                        {(returnVideoFile?.size / (1024 * 1024)).toFixed(2)} MB
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger py-4 px-10 rounded-6 text-xs fw-bold"
                                                onClick={handleRemoveVideo}
                                            >
                                                <i className="ph ph-trash me-4" /> Remove
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-20">
                                <label className="text-sm fw-medium mb-6 d-block">Additional Comments</label>
                                <textarea className="common-input" rows={3} placeholder="Please provide details about the issue..." value={returnComments} onChange={(e) => setReturnComments(e.target.value)} required />
                            </div>
                            <div className="flex-align gap-12 justify-content-end">
                                <button
                                    type="button"
                                    className="py-10 px-22 rounded-8 text-sm fw-bold transition"
                                    style={{ background: '#F1F5F9', border: '1.5px solid #CBD5E1', color: '#1E293B', cursor: 'pointer' }}
                                    onClick={() => { setSelectedOrderForReturn(null); handleRemoveVideo(); }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-main py-10 px-24 rounded-8" disabled={isSubmittingReturn}>
                                    {isSubmittingReturn ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="container container-lg">
                <div className="row g-24 g-lg-32 gy-4">
                    {/* Left Sidebar (Desktop Only) */}
                    <div className="d-none d-lg-block col-lg-3">
                        <div className="acc-sidebar-card">
                            <div className="text-center">
                                <div className="acc-avatar">{(profile.name?.charAt(0) || 'U').toUpperCase()}</div>
                                <h6 className="mb-4">{profile.name}</h6>
                                <p className="text-gray-500 text-sm mb-0">{profile.email}</p>
                            </div>
                            <hr className="my-24" />
                            <nav>
                                {navItems.map((item) => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className={`acc-nav-item ${activeTab === item.key ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveTab(item.key);
                                            navigate(`/account?tab=${item.key}`, { replace: true });
                                        }}
                                    >
                                        <i className={`ph ${item.icon}`} style={{ fontSize: 18, minWidth: 20, textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                            <button type="button" className="acc-logout-btn" onClick={() => setShowLogoutModal(true)}>
                                <i className="ph ph-sign-out" /> Logout
                            </button>
                        </div>
                    </div>

                    {/* Right Content */}
                    <div className="col-lg-9">
                        {/* Mobile Horizontal Tabs (< 992px) */}
                        <div className="d-block d-lg-none acc-mobile-tabs-container">
                            <div className="acc-mobile-tabs">
                                {navItems.map((item) => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className={`acc-mobile-tab-btn ${activeTab === item.key ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveTab(item.key);
                                            navigate(`/account?tab=${item.key}`, { replace: true });
                                        }}
                                    >
                                        <i className={`ph ${item.icon}`} />
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="acc-content-card">
                            {/* Profile Tab */}
                            {activeTab === 'profile' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                            >
                                                <i className="ph-fill ph-user-circle text-2xl" />
                                            </div>
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">Personal Information</h5>
                                                <p className="text-xs text-gray-500 mb-0">Manage your profile and contact details</p>
                                            </div>
                                        </div>
                                        {!isEditingProfile && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-main py-8 px-20 rounded-pill text-xs fw-bold d-flex align-items-center gap-6 shadow-xs"
                                                onClick={() => setIsEditingProfile(true)}
                                            >
                                                <i className="ph-bold ph-pencil text-sm" /> Edit Profile
                                            </button>
                                        )}
                                    </div>

                                    {!isEditingProfile ? (
                                        <div className="rounded-16 border border-gray-200 overflow-hidden bg-white shadow-xs">
                                            {/* Full Name Row */}
                                            <div className="p-16 p-sm-20 d-flex align-items-center justify-content-between flex-wrap gap-12">
                                                <div className="d-flex align-items-center gap-14">
                                                    <div
                                                        className="rounded-10 flex-center flex-shrink-0"
                                                        style={{ width: 40, height: 40, background: '#F1F5F9', color: '#0F172A' }}
                                                    >
                                                        <i className="ph-bold ph-user text-lg" />
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-500 fw-semibold text-uppercase d-block mb-2" style={{ letterSpacing: '0.04em' }}>
                                                            Full Name
                                                        </span>
                                                        <span className="text-sm text-sm-base fw-bold text-neutral-900">
                                                            {profile.name || 'Not provided'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div className="border-top border-gray-100" />

                                            {/* Email Address Row */}
                                            <div className="p-16 p-sm-20 d-flex align-items-center justify-content-between flex-wrap gap-12">
                                                <div className="d-flex align-items-center gap-14 min-w-0">
                                                    <div
                                                        className="rounded-10 flex-center flex-shrink-0"
                                                        style={{ width: 40, height: 40, background: '#F1F5F9', color: '#0F172A' }}
                                                    >
                                                        <i className="ph-bold ph-envelope text-lg" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <span className="text-xs text-gray-500 fw-semibold text-uppercase d-block mb-2" style={{ letterSpacing: '0.04em' }}>
                                                            Email Address
                                                        </span>
                                                        <span className="text-sm text-sm-base fw-bold text-neutral-900 text-truncate d-block" title={profile.email}>
                                                            {profile.email || 'Not provided'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="badge rounded-pill px-10 py-5 text-[11px] fw-bold flex-shrink-0 ms-auto" style={{ background: '#DCFCE7', color: '#15803D' }}>
                                                    <i className="ph-bold ph-check me-4" /> Verified
                                                </span>
                                            </div>

                                            {/* Divider */}
                                            <div className="border-top border-gray-100" />

                                            {/* Phone Number Row */}
                                            <div className="p-16 p-sm-20 d-flex align-items-center justify-content-between flex-wrap gap-12">
                                                <div className="d-flex align-items-center gap-14">
                                                    <div
                                                        className="rounded-10 flex-center flex-shrink-0"
                                                        style={{ width: 40, height: 40, background: '#F1F5F9', color: '#0F172A' }}
                                                    >
                                                        <i className="ph-bold ph-phone text-lg" />
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-500 fw-semibold text-uppercase d-block mb-2" style={{ letterSpacing: '0.04em' }}>
                                                            Phone Number
                                                        </span>
                                                        <span className="text-sm text-sm-base fw-bold text-neutral-900">
                                                            {profile.phone || <span className="text-gray-400 fw-normal text-sm">Not provided</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={saveProfile} className="rounded-16 border border-gray-200 p-20 p-sm-28 bg-white shadow-xs">
                                            <h6 className="text-sm fw-bold text-neutral-900 mb-20 font-heading">Edit Profile Details</h6>
                                            <div className="row g-16 g-sm-20">
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Full Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={profileDraft.name}
                                                        onChange={(e) => setProfileDraft({ ...profileDraft, name: e.target.value })}
                                                        placeholder="Enter your full name"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Phone Number
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={profileDraft.phone}
                                                        onChange={(e) => setProfileDraft({ ...profileDraft, phone: e.target.value })}
                                                        placeholder="Enter 10-digit mobile number"
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Email Address
                                                    </label>
                                                    <input
                                                        type="email"
                                                        className="common-input py-12 px-16 rounded-10 text-sm bg-gray-50 text-gray-500"
                                                        value={profile.email}
                                                        disabled
                                                    />
                                                    <span className="text-[11px] text-gray-400 mt-4 d-block">
                                                        <i className="ph ph-lock me-4" /> Email address cannot be changed directly.
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-12 mt-28 pt-20 border-top border-gray-100">
                                                <button type="submit" className="btn btn-main py-12 px-28 rounded-10 text-xs fw-bold shadow-sm">
                                                    Save Changes
                                                </button>
                                                <button
                                                    type="button"
                                                    className="rounded-10 px-20 py-12 text-xs fw-bold transition"
                                                    style={{ background: '#F1F5F9', border: '1.5px solid #94A3B8', color: '#0F172A', cursor: 'pointer' }}
                                                    onClick={() => setIsEditingProfile(false)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </>
                            )}

                            {/* My Orders Tab */}
                            {activeTab === 'orders' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                            >
                                                <i className="ph-fill ph-shopping-bag text-2xl" />
                                            </div>
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">My Orders</h5>
                                                <p className="text-xs text-gray-500 mb-0">Track shipments, manage active orders, and view past purchases</p>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-10">
                                            <span className="badge rounded-pill bg-gray-100 text-gray-700 py-6 px-12 text-xs fw-bold">
                                                {orders.length} Order{orders.length !== 1 ? 's' : ''}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-main py-6 px-14 rounded-pill text-xs fw-bold d-flex align-items-center gap-6 shadow-xs"
                                                onClick={fetchOrders}
                                            >
                                                <i className="ph-bold ph-arrows-clockwise text-sm" /> Refresh
                                            </button>
                                        </div>
                                    </div>

                                    {isLoadingOrders ? (
                                        <div className="text-center py-60"><p className="text-gray-500">Loading your orders...</p></div>
                                    ) : orders.length === 0 ? (
                                        <div className="text-center py-60">
                                            <i className="ph ph-shopping-bag-open text-gray-300" style={{ fontSize: 56 }} />
                                            <p className="mt-16 mb-20 text-gray-500">You haven't placed any orders yet.</p>
                                            <Link to="/shop" className="btn btn-main py-12 px-28 rounded-8">Start Shopping</Link>
                                        </div>
                                    ) : (
                                        orders.map((order) => {
                                            const statusInfo = getStatusBadgeStyle(order.status);
                                            const canCancel = isOrderCancellable(order);
                                            const timeRemaining = canCancel ? getCancellationTimeRemaining(order) : null;
                                            const canReturn = order.status === 'delivered';
                                            const firstItem = order.items?.[0];
                                            const totalItemCount = order.items?.length || 0;
                                            const moreItemsCount = totalItemCount > 1 ? totalItemCount - 1 : 0;
                                            const orderIdentifier = order.referenceSlug || order.id;

                                            return (
                                                <div className="order-card" key={order.id}>
                                                    <div className="flex-between flex-wrap gap-12 pb-16 mb-16 border-bottom">
                                                        <div>
                                                            <div className="flex-align gap-10 flex-wrap">
                                                                <span className="fw-bold text-neutral-900 text-md">
                                                                    Order #{order.referenceSlug || order.id}
                                                                </span>
                                                                <span className="order-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                                                                    {statusInfo.label}
                                                                </span>
                                                                {canCancel && timeRemaining && (
                                                                    <span className="badge rounded-pill bg-danger-50 text-danger-700 border border-danger-200 py-3 px-8 text-[11px] fw-bold d-inline-flex align-items-center gap-4">
                                                                        <i className="ph-bold ph-clock text-xs" /> {timeRemaining}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-gray-500 mt-2 d-block">
                                                                Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm-end">
                                                            <span className="fw-bold text-neutral-900 text-lg d-block">₹{parseFloat(order.totalAmount || 0).toFixed(2)}</span>
                                                            <span className="text-xs text-gray-500 text-uppercase">{order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Prepaid (Online)'}</span>
                                                        </div>
                                                    </div>

                                                    {/* Single Product Preview */}
                                                    {firstItem ? (
                                                        <Link
                                                            to={`/order-details/${orderIdentifier}`}
                                                            className="d-flex align-items-center justify-content-between flex-wrap gap-14 mb-16 p-12 rounded-12 border border-gray-100 bg-gray-50 text-neutral-900 transition hover-border-main-600 d-block"
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            <div className="d-flex align-items-center gap-14 min-w-0 flex-grow-1">
                                                                <img
                                                                    src={formatImageUrl(firstItem.image)}
                                                                    alt={firstItem.productName || 'Product'}
                                                                    className="order-item-thumb"
                                                                    onError={(e) => {
                                                                        e.currentTarget.onerror = null;
                                                                        e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                                    }}
                                                                />
                                                                <div className="min-w-0">
                                                                    <p className="mb-2 text-sm fw-bold text-neutral-900 text-line-1">{firstItem.productName || 'Healthy Homes Product'}</p>
                                                                    {firstItem.selectedVariantName && (
                                                                        <span className="text-xs text-gray-500 d-block mb-2">Variant: {firstItem.selectedVariantName}</span>
                                                                    )}
                                                                    <div className="d-flex align-items-center gap-8 text-xs text-gray-600 flex-wrap">
                                                                        <span>Qty: <strong>{firstItem.quantity}</strong></span>
                                                                        <span>•</span>
                                                                        <span>₹{parseFloat(firstItem.price || 0).toFixed(2)} each</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {moreItemsCount > 0 && (
                                                                <div className="flex-shrink-0">
                                                                    <span className="badge rounded-pill bg-white text-neutral-800 border border-gray-200 py-6 px-12 text-xs fw-bold shadow-xs">
                                                                        +{moreItemsCount} more item{moreItemsCount > 1 ? 's' : ''}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </Link>
                                                    ) : (
                                                        <div className="text-xs text-gray-400 mb-16">No item details available</div>
                                                    )}

                                                    {/* Courier Tracking Details */}
                                                    {order.courierName && (
                                                        <div className="p-12 rounded-8 mb-16" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px' }}>
                                                            <div className="flex-align gap-8 text-gray-700">
                                                                <i className="ph ph-truck text-main-600" />
                                                                <span>Courier: <strong>{order.courierName}</strong></span>
                                                                {order.awbCode && <span>• AWB: <strong>{order.awbCode}</strong></span>}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Actions */}
                                                    <div className="flex-align justify-content-between flex-wrap gap-12 pt-8">
                                                        <div className="text-xs text-gray-500">
                                                            {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'} in this order
                                                        </div>
                                                        <div className="d-flex align-items-center gap-8">
                                                            {canCancel && (
                                                                <button
                                                                    type="button"
                                                                    className="btn-cancel-order"
                                                                    onClick={() => handleCancelOrder(order)}
                                                                >
                                                                    <i className="ph-bold ph-x-circle" /> Cancel Order
                                                                </button>
                                                            )}
                                                            {((order.status || '').toLowerCase() === 'delivered') && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-main py-8 px-16 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6"
                                                                    onClick={() => {
                                                                        setActiveTab('reviews');
                                                                        navigate('/account?tab=reviews', { replace: true });
                                                                    }}
                                                                >
                                                                    <i className="ph-bold ph-star text-sm" /> Review Items
                                                                </button>
                                                            )}
                                                            <Link
                                                                to={`/order-details/${orderIdentifier}`}
                                                                className="btn btn-sm btn-main py-8 px-20 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6 shadow-xs"
                                                            >
                                                                <i className="ph-bold ph-eye text-sm" /> View Details
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </>
                            )}

                            {/* Returns & Refunds Tab (Disabled) */}
                            {false && activeTab === 'returns' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                            >
                                                <i className="ph-fill ph-arrow-u-up-left text-2xl" />
                                            </div>
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">Returns & Refunds</h5>
                                                <p className="text-xs text-gray-500 mb-0">Track real-time progress of return requests, quality checks, and refund settlements</p>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-10">
                                            <span className="badge rounded-pill bg-gray-100 text-gray-700 py-6 px-12 text-xs fw-bold">
                                                {returnsList.length} Request{returnsList.length !== 1 ? 's' : ''}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-main py-6 px-14 rounded-pill text-xs fw-bold d-flex align-items-center gap-6 shadow-xs"
                                                onClick={fetchReturns}
                                            >
                                                <i className="ph-bold ph-arrows-clockwise text-sm" /> Refresh
                                            </button>
                                        </div>
                                    </div>

                                    {isLoadingReturns ? (
                                        <div className="text-center py-60">
                                            <div className="spinner-border text-main-600 mb-12" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="text-sm text-gray-500">Loading your return requests...</p>
                                        </div>
                                    ) : returnsList.length === 0 ? (
                                        <div className="text-center py-60 px-20 rounded-16 bg-gray-50 border border-gray-200">
                                            <div className="flex-center rounded-circle bg-white shadow-xs mx-auto mb-16" style={{ width: 68, height: 68, color: '#2FA84F' }}>
                                                <i className="ph-fill ph-arrow-u-up-left" style={{ fontSize: 32 }} />
                                            </div>
                                            <h6 className="fw-bold text-neutral-900 mb-6">No Return Requests Found</h6>
                                            <p className="text-xs text-gray-500 mb-20 max-w-350 mx-auto">
                                                You haven't requested any returns or refunds yet. All your past purchases and eligible return options are available under My Orders.
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-main py-10 px-24 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6"
                                                onClick={() => {
                                                    setActiveTab('orders');
                                                    navigate('/account?tab=orders', { replace: true });
                                                }}
                                            >
                                                <i className="ph-bold ph-shopping-bag" /> View My Orders
                                            </button>
                                        </div>
                                    ) : (
                                        returnsList.map((ret, idx) => {
                                            const statusBadge = getReturnStatusBadge(ret.status);
                                            const stageNum = getReturnStageNumber(ret.status);
                                            const item = ret.orderItem || {};
                                            const orderRef = ret.order?.referenceSlug || ret.order?.order_number || ret.orderId;
                                            const videoMedia = (ret.media || []).find(m => m.mediaType === 'video' || (m.filePath && m.filePath.match(/\.(mp4|mov|webm)$/i)));
                                            const isRejected = ret.status === 'rejected';
                                            const itemPrice = parseFloat(item.price || item.salesPrice || 0);
                                            const returnQty = ret.returnQuantity || 1;
                                            const totalReturnValue = itemPrice * returnQty;
                                            const progressPct = stageNum === 1 ? 0 : stageNum === 2 ? 25 : stageNum === 3 ? 50 : stageNum === 4 ? 75 : 100;

                                            return (
                                                <div className="rt-card" key={ret.id || idx}>
                                                    {/* Card Top Header */}
                                                    <div className="rt-card-top d-flex align-items-center justify-content-between flex-wrap gap-12">
                                                        <div className="d-flex align-items-center gap-10 flex-wrap">
                                                            <span className="badge rounded-pill bg-neutral-900 text-white py-6 px-14 text-xs fw-bold d-inline-flex align-items-center gap-6 shadow-xs">
                                                                <i className="ph-bold ph-arrow-u-up-left text-xs text-success-400" />
                                                                Return #{ret.referenceSlug || (ret.id && ret.id.slice(0, 8).toUpperCase()) || `RET-${idx + 1}`}
                                                            </span>
                                                            <Link
                                                                to={`/order-details/${orderRef}`}
                                                                className="badge rounded-pill bg-white text-main-600 border border-main-200 py-6 px-12 text-xs fw-bold text-decoration-none hover-bg-main-50 d-inline-flex align-items-center gap-4 transition shadow-xs"
                                                            >
                                                                <i className="ph ph-receipt text-xs" /> Order #{orderRef}
                                                            </Link>
                                                            <span className="badge rounded-pill bg-white text-gray-700 border border-gray-200 py-5 px-10 text-[11px] fw-bold text-uppercase d-inline-flex align-items-center gap-4">
                                                                <i className="ph-bold ph-credit-card text-main-600" /> Refund
                                                            </span>
                                                        </div>

                                                        <div className="d-flex align-items-center gap-10 flex-wrap">
                                                            <span
                                                                className="order-status-badge d-inline-flex align-items-center shadow-xs"
                                                                style={{ background: statusBadge.bg, color: statusBadge.color, fontSize: '11px' }}
                                                            >
                                                                <span className="rt-pill-dot" style={{ background: statusBadge.color }} />
                                                                {statusBadge.label}
                                                            </span>
                                                            <span className="text-xs text-gray-500 d-inline-flex align-items-center gap-4">
                                                                <i className="ph ph-calendar text-xs" />
                                                                {new Date(ret.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* 5-Stage Stepper */}
                                                    {isRejected ? (
                                                        <div className="p-20 bg-danger-50 border-bottom border-danger-100 flex-align gap-12">
                                                            <div className="flex-center rounded-circle bg-danger-600 text-white flex-shrink-0" style={{ width: 36, height: 36 }}>
                                                                <i className="ph-bold ph-x text-lg" />
                                                            </div>
                                                            <div>
                                                                <strong className="text-danger-800 text-sm d-block mb-2">Return Request Declined</strong>
                                                                <span className="text-xs text-danger-700">
                                                                    {ret.rejectedReason ? ret.rejectedReason : 'The request does not meet the product return conditions.'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="rt-stepper-wrap">
                                                            <div className="rt-stepper-line">
                                                                <div className="rt-stepper-track-bg" />
                                                                <div className="rt-stepper-track-fill" style={{ width: `calc((100% - 80px) * ${progressPct / 100})` }} />
                                                                {RETURN_STAGES.map((stg) => {
                                                                    const isDone = stageNum > stg.id || (stageNum === 5 && stg.id === 5);
                                                                    const isActive = stageNum === stg.id && stageNum < 5;

                                                                    return (
                                                                        <div key={stg.id} className="rt-step-item" style={{ opacity: (isDone || isActive) ? 1 : 0.45 }}>
                                                                            <div className={`rt-step-circle ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                                                                                {isDone ? <i className="ph-bold ph-check" /> : stg.id}
                                                                            </div>
                                                                            <div className="rt-node-title">{stg.label}</div>
                                                                            <div className="rt-node-desc">{stg.desc}</div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Card Body */}
                                                    <div className="p-16 p-sm-22">
                                                        {/* Product Item Box */}
                                                        <div className="rt-product-box d-flex align-items-center justify-content-between flex-wrap gap-16 mb-18">
                                                            <div className="d-flex align-items-center gap-14 min-w-0 flex-grow-1">
                                                                <img
                                                                    src={formatImageUrl(item.image)}
                                                                    alt={item.productName || 'Product'}
                                                                    className="rt-product-thumb"
                                                                    onError={(e) => {
                                                                        e.currentTarget.onerror = null;
                                                                        e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                                    }}
                                                                />
                                                                <div className="min-w-0">
                                                                    <h6 className="text-sm fw-bold text-neutral-900 mb-4 text-line-1">
                                                                        {item.productName || 'Healthy Homes Product'}
                                                                    </h6>
                                                                    <div className="d-flex align-items-center gap-8 flex-wrap mb-4">
                                                                        {item.selectedVariantName && (
                                                                            <span className="badge bg-white text-neutral-800 border border-gray-200 py-3 px-8 text-xs fw-semibold">
                                                                                {item.selectedVariantName.replace(/^Variant:\s*/i, '')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        Quantity: <strong className="text-neutral-900">{returnQty} unit{returnQty > 1 ? 's' : ''}</strong>
                                                                        <span className="mx-6 text-gray-300">•</span>
                                                                        Unit Price: <strong className="text-neutral-900">₹{itemPrice.toFixed(2)}</strong>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="text-sm-end flex-shrink-0 bg-white p-8 px-12 rounded-8">
                                                                <span className="text-xs text-gray-500 d-block text-uppercase fw-semibold mb-1" style={{ letterSpacing: '0.04em'}}>
                                                                    Estimated Value
                                                                </span>
                                                                <span className="text-xs fw-bold text-main-600">
                                                                    ₹{totalReturnValue.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Details Grid: Reason, Video Proof, Settlement */}
                                                        <div className="row g-16">
                                                            {/* Reason & Comments */}
                                                            <div className="col-12 col-md-6">
                                                                <div className="rt-info-card">
                                                                    <div>
                                                                        <div className="d-flex align-items-center justify-content-between mb-8">
                                                                            <span className="text-xs text-gray-500 fw-bold text-uppercase d-flex align-items-center gap-6" style={{ letterSpacing: '0.04em' }}>
                                                                                <i className="ph-bold ph-info text-main-600 text-sm" /> Return Reason
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-sm fw-bold text-neutral-900 mb-8">
                                                                            {ret.reason ? ret.reason.replace(/_/g, ' ') : 'Damaged Product'}
                                                                        </div>
                                                                        {ret.comments ? (
                                                                            <div className="p-10 rounded-8 bg-gray-50 border border-gray-100 text-xs text-gray-600 fst-italic">
                                                                                <span className="fw-semibold text-neutral-800 me-4">Note:</span> "{ret.comments}"
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-xs text-gray-400">No additional note provided</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Unboxing Video Proof */}
                                                            <div className="col-12 col-md-6">
                                                                <div className="rt-info-card">
                                                                    <div>
                                                                        <div className="d-flex align-items-center justify-content-between mb-8">
                                                                            <span className="text-xs text-gray-500 fw-bold text-uppercase d-flex align-items-center gap-6" style={{ letterSpacing: '0.04em' }}>
                                                                                <i className="ph-fill ph-video-camera text-main-600 text-sm" /> Mandatory Proof
                                                                            </span>
                                                                            {videoMedia && (
                                                                                <span className="badge rounded-pill bg-success-50 text-success-700 border border-success-200 py-3 px-8 text-[11px] fw-bold d-inline-flex align-items-center gap-4">
                                                                                    <i className="ph-bold ph-check text-xs" /> Attached
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-xs text-gray-600 mb-12">
                                                                            {videoMedia ? 'Package Unboxing Video is attached and verified.' : 'No unboxing video uploaded.'}
                                                                        </div>
                                                                    </div>
                                                                    {videoMedia ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-main py-8 px-14 rounded-8 text-xs fw-bold d-flex align-items-center justify-content-center gap-6 w-100 transition shadow-xs"
                                                                            onClick={() => setPreviewVideoModal(formatImageUrl(videoMedia.filePath))}
                                                                        >
                                                                            <i className="ph-fill ph-play-circle text-base" /> Watch Unboxing Video
                                                                        </button>
                                                                    ) : (
                                                                        <div className="p-8 rounded-8 bg-gray-50 text-center text-xs text-gray-400">
                                                                            No video available
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Refund Settlement Box (if applicable) */}
                                                            {ret.refund && (
                                                                <div className="col-12">
                                                                    <div className="p-14 rounded-12 border border-success-200 bg-success-50 d-flex align-items-center justify-content-between flex-wrap gap-12">
                                                                        <div className="d-flex align-items-center gap-10">
                                                                            <div className="flex-center rounded-circle bg-success-600 text-white flex-shrink-0" style={{ width: 36, height: 36 }}>
                                                                                <i className="ph-bold ph-currency-inr text-base" />
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-xs fw-bold text-success-900 d-block">
                                                                                    Refund Amount: ₹{parseFloat(ret.refund.refundAmount || 0).toFixed(2)}
                                                                                </span>
                                                                                <span className="text-[11px] text-success-700">
                                                                                    Status: <strong>{ret.refund.refundStatus?.toUpperCase()}</strong> {ret.refund.refundMode ? `via ${ret.refund.refundMode.toUpperCase()}` : ''}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        {ret.refund.razorpayRefundId && (
                                                                            <span className="badge bg-white text-success-800 border border-success-300 py-5 px-10 text-xs font-monospace">
                                                                                Ref: {ret.refund.razorpayRefundId}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Card Bottom Bar */}
                                                    <div className="px-20 py-14 bg-gray-50 border-top border-gray-100 d-flex align-items-center justify-content-between flex-wrap gap-10">
                                                        <div className="d-flex align-items-center gap-6 text-xs text-gray-500">
                                                            <i className="ph-bold ph-shield-check text-main-600 text-sm" />
                                                            <span>Our verification team reviews return packages upon courier receipt.</span>
                                                        </div>
                                                        <Link
                                                            to={`/order-details/${orderRef}`}
                                                            className="btn btn-sm btn-main py-8 px-18 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6 shadow-xs"
                                                        >
                                                            View Full Order <i className="ph-bold ph-arrow-right text-xs" />
                                                        </Link>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </>
                            )}

                            {/* Reviews & Ratings Tab */}
                            {activeTab === 'reviews' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            {/* <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                                            >
                                                <i className="ph-fill ph-star text-2xl" />
                                            </div> */}
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">My Reviews & Ratings</h5>
                                                <p className="text-xs text-gray-500 mb-0">Rate your delivered purchases and manage your submitted reviews</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-main py-6 px-14 rounded-pill text-xs fw-bold d-flex align-items-center gap-6 shadow-xs"
                                            onClick={fetchReviewData}
                                        >
                                            <i className="ph-bold ph-arrows-clockwise text-sm" /> Refresh
                                        </button>
                                    </div>

                                    {/* Summary Metric Cards */}
                                    <div className="review-summary-grid">
                                        <div className="review-metric-card">
                                            <div className="rounded-10 flex-center flex-shrink-0" style={{ width: 40, height: 40, background: '#FEF3C7', color: '#B45309' }}>
                                                <i className="ph-bold ph-pencil-simple-line text-lg" />
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 fw-semibold d-block">To Review</span>
                                                <h5 className="mb-0 fw-bold text-neutral-900">{purchasedReviewData.counts?.readyToReview || 0}</h5>
                                            </div>
                                        </div>
                                        <div className="review-metric-card">
                                            <div className="rounded-10 flex-center flex-shrink-0" style={{ width: 40, height: 40, background: '#E0E7FF', color: '#4338CA' }}>
                                                <i className="ph-bold ph-chat-circle-dots text-lg" />
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 fw-semibold d-block">Submitted</span>
                                                <h5 className="mb-0 fw-bold text-neutral-900">{myReviews.length || 0}</h5>
                                            </div>
                                        </div>
                                        <div className="review-metric-card">
                                            <div className="rounded-10 flex-center flex-shrink-0" style={{ width: 40, height: 40, background: '#DCFCE7', color: '#15803D' }}>
                                                <i className="ph-bold ph-check-circle text-lg" />
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 fw-semibold d-block">Approved</span>
                                                <h5 className="mb-0 fw-bold text-neutral-900">{myReviews.filter(r => r.status === 'Approved').length || 0}</h5>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sub-tab Switcher */}
                                    <div className="review-subtab-bar">
                                        <button
                                            type="button"
                                            className={`review-subtab-btn ${reviewsSubTab === 'to_review' ? 'active' : ''}`}
                                            onClick={() => setReviewsSubTab('to_review')}
                                        >
                                            <i className="ph ph-clock-countdown text-base" />
                                            To Review ({purchasedReviewData.counts?.readyToReview || 0})
                                        </button>
                                        <button
                                            type="button"
                                            className={`review-subtab-btn ${reviewsSubTab === 'my_reviews' ? 'active' : ''}`}
                                            onClick={() => setReviewsSubTab('my_reviews')}
                                        >
                                            <i className="ph ph-chat-teardrop-text text-base" />
                                            My Reviews ({myReviews.length || 0})
                                        </button>
                                    </div>

                                    {isLoadingReviews ? (
                                        <div className="text-center py-60">
                                            <div className="spinner-border text-main-600 mb-12" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="text-sm text-gray-500">Loading your purchases & reviews...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Subtab 1: To Review (Delivered & Awaiting Review) */}
                                            {reviewsSubTab === 'to_review' && (
                                                <div>
                                                    {purchasedReviewData.items.filter(item => item.canReview).length === 0 ? (
                                                        <div className="text-center rounded-16 bg-gray-50 border border-gray-200" style={{padding : "15px 0px"}}>
                                                            <div className="flex-center rounded-circle bg-white shadow-xs mx-auto mb-16" style={{ width: 68, height: 68, color: '#2FA84F' }}>
                                                                <i className="ph-fill ph-check-circle" style={{ fontSize: 32 }} />
                                                            </div>
                                                            <h6 className="fw-bold text-neutral-900 mb-6">All Caught Up!</h6>
                                                            <p className="text-xs text-gray-500 mb-20 max-w-380 mx-auto">
                                                                You don't have any delivered items waiting for review. Once your orders are delivered, they will appear here!
                                                            </p>
                                                            <div className="d-flex align-items-center justify-content-center gap-12 flex-wrap">
                                                                {myReviews.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-main py-10 px-20 rounded-8 text-xs fw-bold"
                                                                        onClick={() => setReviewsSubTab('my_reviews')}
                                                                    >
                                                                        View My Submitted Reviews
                                                                    </button>
                                                                )}
                                                                <Link to="/shop" className="btn btn-main py-10 px-24 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6">
                                                                    <i className="ph-bold ph-storefront" /> Explore Products
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        purchasedReviewData.items
                                                            .filter(item => item.canReview)
                                                            .map((item) => (
                                                                <div key={item.key} className="review-card-item">
                                                                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-16">
                                                                        <div className="d-flex align-items-start gap-16 min-w-0 flex-grow-1">
                                                                            <img
                                                                                src={formatImageUrl(item.image)}
                                                                                alt={item.name}
                                                                                className="review-thumb"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.onerror = null;
                                                                                    e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                                                }}
                                                                            />
                                                                            <div className="min-w-0 flex-grow-1">
                                                                                <div className="d-flex align-items-center gap-8 mb-4 flex-wrap">
                                                                                    <span className="badge rounded-pill" style={{ background: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0', fontSize: '11px', padding: '4px 10px', fontWeight: 600 }}>
                                                                                        <i className="ph-fill ph-check-circle me-4" /> Delivered
                                                                                    </span>
                                                                                    {item.isCombo && (
                                                                                        <span className="badge rounded-pill" style={{ background: '#E0E7FF', color: '#4338CA', border: '1px solid #C7D2FE', fontSize: '11px', padding: '4px 10px', fontWeight: 600 }}>
                                                                                            Combo Pack
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <h6 className="text-sm fw-bold text-neutral-900 mb-4">
                                                                                    {item.slug ? (
                                                                                        <Link to={`/product-details/${item.slug}`} className="text-neutral-900 hover-text-main-600">
                                                                                            {item.name}
                                                                                        </Link>
                                                                                    ) : (
                                                                                        item.name
                                                                                    )}
                                                                                </h6>
                                                                                {item.selectedVariantName && (
                                                                                    <span className="text-xs text-gray-500 d-block mb-4">
                                                                                        Variant: <strong>{item.selectedVariantName}</strong>
                                                                                    </span>
                                                                                )}
                                                                                <div className="text-xs text-gray-400">
                                                                                    Purchased on {new Date(item.lastOrderDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-shrink-0 d-flex flex-column align-items-sm-end gap-8 w-100 w-sm-auto">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-main py-10 px-22 rounded-10 text-xs fw-bold d-inline-flex align-items-center justify-content-center gap-8 shadow-xs w-100 w-sm-auto"
                                                                                onClick={() => openCreateReviewModal(item)}
                                                                            >
                                                                                <i className="ph-bold ph-star text-sm" /> Write Review
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                            )}

                                            {/* Subtab 2: My Reviews (Submitted Reviews & Status) */}
                                            {reviewsSubTab === 'my_reviews' && (
                                                <div>
                                                    {myReviews.length === 0 ? (
                                                        <div className="text-center py-50 px-20 rounded-16 bg-gray-50 border border-gray-200">
                                                            <div className="flex-center rounded-circle bg-white shadow-xs mx-auto mb-16" style={{ width: 68, height: 68, color: '#F59E0B' }}>
                                                                <i className="ph-fill ph-chat-teardrop-dots" style={{ fontSize: 32 }} />
                                                            </div>
                                                            <h6 className="fw-bold text-neutral-900 mb-6">No Reviews Submitted Yet</h6>
                                                            <p className="text-xs text-gray-500 mb-20 max-w-380 mx-auto">
                                                                Share your genuine feedback on your delivered purchases to help other customers make informed decisions!
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="btn btn-main py-10 px-24 rounded-8 text-xs fw-bold d-inline-flex align-items-center gap-6"
                                                                onClick={() => setReviewsSubTab('to_review')}
                                                            >
                                                                <i className="ph-bold ph-pencil-simple-line" /> Review Delivered Purchases
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        myReviews.map((rev) => {
                                                            const badge = getReviewStatusBadge(rev.status);
                                                            const itemData = rev.product || rev.childCombo || {};
                                                            let rawImg = itemData.image;
                                                            if (Array.isArray(rawImg) && rawImg.length > 0) rawImg = rawImg[0];
                                                            else if (typeof rawImg === 'string') {
                                                                try {
                                                                    const parsed = JSON.parse(rawImg);
                                                                    if (Array.isArray(parsed)) rawImg = parsed[0];
                                                                } catch (err) {}
                                                            }

                                                            return (
                                                                <div key={rev.id} className="review-card-item">
                                                                    {/* Top Header Row */}
                                                                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-12 mb-16 pb-14 border-bottom border-gray-100">
                                                                        <div className="d-flex align-items-center gap-14 min-w-0 flex-grow-1">
                                                                            <img
                                                                                src={formatImageUrl(rawImg)}
                                                                                alt={itemData.name || 'Product'}
                                                                                className="review-thumb"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.onerror = null;
                                                                                    e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                                                }}
                                                                            />
                                                                            <div className="min-w-0">
                                                                                <h6 className="text-sm fw-bold text-neutral-900 mb-4 text-line-1">
                                                                                    {itemData.slug ? (
                                                                                        <Link to={`/product-details/${itemData.slug}`} className="text-neutral-900 hover-text-main-600">
                                                                                            {itemData.name || 'Purchased Item'}
                                                                                        </Link>
                                                                                    ) : (
                                                                                        itemData.name || 'Purchased Item'
                                                                                    )}
                                                                                </h6>
                                                                                <div className="d-flex align-items-center gap-6 text-xs text-gray-500">
                                                                                    <i className="ph ph-calendar-blank text-sm" />
                                                                                    <span>Reviewed on {new Date(rev.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-8 flex-shrink-0">
                                                                            <span
                                                                                className="badge rounded-pill text-xs py-5 px-12 fw-bold d-inline-flex align-items-center gap-6"
                                                                                style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}33` }}
                                                                            >
                                                                                <i className={`ph-fill ${badge.icon} text-sm`} />
                                                                                {badge.label}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    {/* Rating & Content */}
                                                                    <div className="mb-16">
                                                                        <div className="d-flex align-items-center gap-10 mb-10">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                                    <span
                                                                                        key={star}
                                                                                        style={{
                                                                                            color: star <= Math.round(Number(rev.rating) || 5) ? '#F59E0B' : '#E2E8F0',
                                                                                            fontSize: '20px',
                                                                                            lineHeight: 1,
                                                                                        }}
                                                                                    >
                                                                                        ★
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                            <span className="text-xs fw-bold px-8 py-2 rounded-6" style={{ background: '#FEF3C7', color: '#B45309' }}>
                                                                                {parseFloat(rev.rating).toFixed(1)} / 5.0
                                                                            </span>
                                                                        </div>
                                                                        <div className="review-quote-box" style={{ whiteSpace: 'pre-line' }}>
                                                                            "{rev.feedback}"
                                                                        </div>
                                                                    </div>

                                                                    {/* Card Footer Actions */}
                                                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 pt-12 border-top border-gray-100">
                                                                        <div className="d-flex align-items-center gap-6 text-xs text-gray-500">
                                                                            {rev.status === 'Pending' && (
                                                                                <>
                                                                                    <i className="ph-bold ph-clock text-amber-600 text-sm" />
                                                                                    <span>Your review is undergoing quick verification by our team.</span>
                                                                                </>
                                                                            )}
                                                                            {rev.status === 'Approved' && (
                                                                                <>
                                                                                    <i className="ph-bold ph-check-circle text-success-600 text-sm" />
                                                                                    <span>Your review is live and visible on the product page.</span>
                                                                                </>
                                                                            )}
                                                                            {rev.status === 'Rejected' && (
                                                                                <>
                                                                                    <i className="ph-bold ph-warning-circle text-danger-600 text-sm" />
                                                                                    <span>Review did not meet our community guidelines.</span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-10">
                                                                            <button
                                                                                type="button"
                                                                                className="rev-action-btn rev-action-btn-edit"
                                                                                onClick={() => openEditReviewModal(rev)}
                                                                            >
                                                                                <i className="ph-bold ph-pencil-simple text-sm" /> Edit
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="rev-action-btn rev-action-btn-delete"
                                                                                onClick={() => setShowDeleteReviewModal(rev)}
                                                                            >
                                                                                <i className="ph-bold ph-trash-simple text-sm" /> Delete
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            {/* Saved Addresses Tab */}
                            {activeTab === 'addresses' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                            >
                                                <i className="ph-fill ph-map-pin text-2xl" />
                                            </div>
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">Saved Addresses</h5>
                                                <p className="text-xs text-gray-500 mb-0">Manage your delivery and billing addresses</p>
                                            </div>
                                        </div>
                                        {!addressForm && (
                                            <button
                                                type="button"
                                                className="btn btn-main py-8 px-20 rounded-pill text-xs fw-bold d-flex align-items-center gap-6 shadow-xs"
                                                onClick={openAddAddress}
                                            >
                                                <i className="ph-bold ph-plus text-sm" /> Add New Address
                                            </button>
                                        )}
                                    </div>

                                    {addressForm ? (
                                        <form onSubmit={saveAddress} className="rounded-16 border border-gray-200 p-20 p-sm-28 bg-white shadow-xs">
                                            <div className="d-flex align-items-center justify-content-between mb-20">
                                                <h6 className="text-sm fw-bold text-neutral-900 mb-0 font-heading">
                                                    {editingAddressId ? 'Edit Address' : 'Add New Address'}
                                                </h6>
                                                <span className="text-xs text-gray-500">* Required fields</span>
                                            </div>

                                            <div className="row g-16 g-sm-20">
                                                {/* Full Name */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Full Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.fullName}
                                                        onChange={(e) => setAddressForm({ ...addressForm, fullName: e.target.value })}
                                                        placeholder="Enter recipient full name"
                                                        required
                                                    />
                                                </div>

                                                {/* Phone Number */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Phone Number *
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        maxLength={10}
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.phone}
                                                        onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                                                        placeholder="10-digit mobile number"
                                                        required
                                                    />
                                                </div>

                                                {/* Street Address */}
                                                <div className="col-12">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Street Address / House No / Road *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.street}
                                                        onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                                                        placeholder="e.g. 2/306, Blue Hills Avenue, Annur Road"
                                                        required
                                                    />
                                                </div>

                                                {/* Apartment / Landmark */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Apartment, Suite, Landmark
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.apartment}
                                                        onChange={(e) => setAddressForm({ ...addressForm, apartment: e.target.value })}
                                                        placeholder="e.g. Near Bus Stand, Apt 4B"
                                                    />
                                                </div>

                                                {/* City */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        City *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.city}
                                                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                                        placeholder="Enter city / town"
                                                        required
                                                    />
                                                </div>

                                                {/* State */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        State *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.state}
                                                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                                                        placeholder="Enter state"
                                                        required
                                                    />
                                                </div>

                                                {/* Pincode */}
                                                <div className="col-12 col-sm-6">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Pincode *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={6}
                                                        className="common-input py-12 px-16 rounded-10 text-sm"
                                                        value={addressForm.pincode}
                                                        onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                                                        placeholder="6-digit PIN code"
                                                        required
                                                    />
                                                </div>

                                                {/* Address Type Chips */}
                                                <div className="col-12">
                                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>
                                                        Address Type
                                                    </label>
                                                    <div className="d-flex align-items-center gap-10 flex-wrap">
                                                        {[
                                                            { key: 'Home', label: 'Home', icon: 'ph-house' },
                                                            { key: 'Work', label: 'Work', icon: 'ph-briefcase' },
                                                            { key: 'Other', label: 'Other', icon: 'ph-map-pin' }
                                                        ].map((t) => {
                                                            const isSelected = (addressForm.addressType || 'Home') === t.key;
                                                            return (
                                                                <button
                                                                    key={t.key}
                                                                    type="button"
                                                                    onClick={() => setAddressForm({ ...addressForm, addressType: t.key })}
                                                                    className={`btn btn-sm py-8 px-16 rounded-pill text-xs fw-bold d-inline-flex align-items-center gap-6 transition ${
                                                                        isSelected
                                                                            ? 'btn-main text-white'
                                                                            : 'bg-gray-50 text-gray-700 border border-gray-200 hover-bg-gray-100'
                                                                    }`}
                                                                >
                                                                    <i className={`ph-bold ${t.icon}`} /> {t.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Set as Default Checkbox */}
                                                <div className="col-12">
                                                    <label className="d-inline-flex align-items-center gap-10 cursor-pointer user-select-none">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input mt-0"
                                                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            checked={!!addressForm.isDefault}
                                                            onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                                                        />
                                                        <span className="text-xs fw-semibold text-neutral-800">
                                                            Set as default delivery address
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="d-flex align-items-center gap-12 mt-28 pt-20 border-top border-gray-100 flex-wrap">
                                                <button type="submit" className="btn btn-main py-12 px-28 rounded-10 text-xs fw-bold shadow-sm">
                                                    {editingAddressId ? 'Update Address' : 'Save Address'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="rounded-10 px-20 py-12 text-xs fw-bold transition"
                                                    style={{ background: '#F1F5F9', border: '1.5px solid #94A3B8', color: '#0F172A', cursor: 'pointer' }}
                                                    onClick={() => { setAddressForm(null); setEditingAddressId(null); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    ) : addresses.length === 0 ? (
                                        <div className="text-center py-48 px-16 rounded-16 border border-dashed border-gray-300 bg-gray-50">
                                            <div className="flex-center rounded-circle bg-white shadow-xs mx-auto mb-16" style={{ width: 56, height: 56, color: '#2FA84F' }}>
                                                <i className="ph-fill ph-map-pin text-3xl" />
                                            </div>
                                            <h6 className="text-sm fw-bold text-neutral-900 mb-4">No Saved Addresses</h6>
                                            <p className="text-xs text-gray-500 mb-20 max-w-sm mx-auto">
                                                You haven't added any delivery addresses yet. Add one now for faster checkout.
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-main py-10 px-24 rounded-pill text-xs fw-bold shadow-sm"
                                                onClick={openAddAddress}
                                            >
                                                <i className="ph-bold ph-plus me-6" /> Add Your First Address
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="row g-16 g-sm-20">
                                            {addresses.map((addr) => {
                                                const typeKey = (addr.addressType || 'Home').toLowerCase();
                                                const typeIcon = typeKey === 'work' ? 'ph-briefcase' : typeKey === 'other' ? 'ph-map-pin' : 'ph-house';
                                                
                                                return (
                                                    <div key={addr.id} className="col-12 col-md-6">
                                                        <div
                                                            className={`h-100 rounded-16 border p-16 p-sm-20 bg-white d-flex flex-column justify-content-between transition shadow-xs position-relative ${
                                                                addr.isDefault ? 'border-main-600' : 'border-gray-200 hover-border-gray-300'
                                                            }`}
                                                            style={{
                                                                boxShadow: addr.isDefault ? '0 0 0 1.5px #2FA84F, 0 2px 8px rgba(47, 168, 79, 0.08)' : undefined
                                                            }}
                                                        >
                                                            <div>
                                                                {/* Top Badge & Action Icons Row */}
                                                                <div className="d-flex align-items-center justify-content-between gap-8 mb-12 flex-wrap">
                                                                    <div className="d-flex align-items-center gap-6 flex-wrap">
                                                                        <span className="badge rounded-pill bg-gray-100 text-gray-700 py-5 px-10 text-xs fw-bold d-inline-flex align-items-center gap-4 text-capitalize">
                                                                            <i className={`ph-fill ${typeIcon} text-xs`} />
                                                                            {addr.addressType || 'Home'}
                                                                        </span>
                                                                        {addr.isDefault && (
                                                                            <span className="badge rounded-pill bg-success-50 text-success-700 border border-success-200 py-5 px-10 text-xs fw-bold d-inline-flex align-items-center gap-4">
                                                                                <i className="ph-bold ph-check text-xs" /> Default
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="d-flex align-items-center gap-6 ms-auto">
                                                                        <button
                                                                            type="button"
                                                                            className="rounded-8 border border-gray-200 bg-gray-50 text-gray-600 hover-text-main-600 hover-bg-main-50 p-6 flex-center transition"
                                                                            style={{ width: 32, height: 32 }}
                                                                            onClick={() => openEditAddress(addr)}
                                                                            title="Edit Address"
                                                                            aria-label="Edit address"
                                                                        >
                                                                            <i className="ph-bold ph-pencil-simple text-sm" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="rounded-8 border border-gray-200 bg-gray-50 text-gray-500 hover-text-danger-600 hover-bg-danger-50 p-6 flex-center transition"
                                                                            style={{ width: 32, height: 32 }}
                                                                            onClick={() => deleteAddress(addr.id)}
                                                                            title="Delete Address"
                                                                            aria-label="Delete address"
                                                                        >
                                                                            <i className="ph-bold ph-trash text-sm" />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Recipient Name */}
                                                                <h6 className="text-sm text-sm-base fw-bold text-neutral-900 mb-6 text-break">
                                                                    {addr.fullName}
                                                                </h6>

                                                                {/* Street & Location Details */}
                                                                <p className="text-xs text-gray-600 mb-10 text-break lh-base" style={{ minHeight: '36px' }}>
                                                                    {addr.street}
                                                                    {addr.apartment ? `, ${addr.apartment}` : ''}
                                                                    {`, ${addr.city}, ${addr.state} - `}
                                                                    <strong className="text-neutral-900">{addr.pincode}</strong>
                                                                </p>

                                                                {/* Phone Number */}
                                                                <div className="d-flex align-items-center gap-6 text-xs text-gray-600 mb-12">
                                                                    <i className="ph-bold ph-phone text-main-600 text-sm flex-shrink-0" />
                                                                    <span className="fw-medium">{addr.phone}</span>
                                                                </div>
                                                            </div>

                                                            {/* Card Bottom: Set Default or Default Indicator */}
                                                            <div className="pt-12 border-top border-gray-100 mt-4">
                                                                {addr.isDefault ? (
                                                                    <span className="text-[11px] text-success-700 fw-bold d-inline-flex align-items-center gap-4">
                                                                        <i className="ph-fill ph-check-circle text-xs" /> Default Delivery Address
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-main py-4 px-12 rounded-8 text-xs fw-semibold w-100"
                                                                        onClick={() => setDefaultAddress(addr.id)}
                                                                    >
                                                                        Set as Default
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-16 mb-24 pb-20 border-bottom border-gray-100">
                                        <div className="d-flex align-items-center gap-12">
                                            <div
                                                className="rounded-12 flex-center text-white flex-shrink-0"
                                                style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                            >
                                                <i className="ph-fill ph-lock-key text-2xl" />
                                            </div>
                                            <div>
                                                <h5 className="mb-2 fw-bold text-neutral-900 font-heading">Security & Password</h5>
                                                <p className="text-xs text-gray-500 mb-0">Manage your password and protect your account security</p>
                                            </div>
                                        </div>
                                    </div>
                                    <form onSubmit={handleUpdatePassword} className="rounded-16 border border-gray-200 p-20 p-sm-28 bg-white shadow-xs" style={{ maxWidth: 520 }}>
                                        <div className="mb-20">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>Current Password *</label>
                                            <div className="position-relative">
                                                <input
                                                    type={showCurrentPassword ? "text" : "password"}
                                                    className="common-input py-12 px-16 rounded-10 text-sm"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    placeholder="Enter current password"
                                                    required
                                                />
                                                <i className={`ph ${showCurrentPassword ? 'ph-eye-slash' : 'ph-eye'} position-absolute top-50 translate-middle-y end-0 me-16 cursor-pointer text-gray-500`} onClick={() => setShowCurrentPassword(!showCurrentPassword)} />
                                            </div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>New Password *</label>
                                            <div className="position-relative">
                                                <input
                                                    type={showNewPassword ? "text" : "password"}
                                                    className="common-input py-12 px-16 rounded-10 text-sm"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="Enter at least 6 characters"
                                                    required
                                                />
                                                <i className={`ph ${showNewPassword ? 'ph-eye-slash' : 'ph-eye'} position-absolute top-50 translate-middle-y end-0 me-16 cursor-pointer text-gray-500`} onClick={() => setShowNewPassword(!showNewPassword)} />
                                            </div>
                                        </div>
                                        <div className="mb-28">
                                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8 d-block" style={{ letterSpacing: '0.04em' }}>Confirm New Password *</label>
                                            <div className="position-relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    className="common-input py-12 px-16 rounded-10 text-sm"
                                                    value={confirmNewPassword}
                                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                                    placeholder="Re-enter new password"
                                                    required
                                                />
                                                <i className={`ph ${showConfirmPassword ? 'ph-eye-slash' : 'ph-eye'} position-absolute top-50 translate-middle-y end-0 me-16 cursor-pointer text-gray-500`} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                                            </div>
                                        </div>
                                        <button type="submit" className="btn btn-main py-12 px-28 rounded-10 text-xs fw-bold shadow-sm" disabled={isUpdatingPassword}>
                                            {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Account;
